import React, { FunctionComponent, ReactElement, useState } from 'react';
import styled from 'styled-components';
import { useMedia, createMediaMatcher } from 'react-media-match';
import { FormattedMessage, useIntl, defineMessages } from 'react-intl';
import Notification from '@empiriecom/module-components/Notification';
import { Modal } from '@empiriecom/module-components/Modal';
import { Esi } from '@empiriecom/module-components/Esi';
import useConfig from '@empiriecom/module-components/hooks/useConfig';
import useTracking from '@empiriecom/module-components/hooks/useTracking';
import { FragmentConfiguration } from '@/config';
import useUrlParameters from '../hooks/useUrlParameters';

const messages = defineMessages({
  modalHeadline: {
    defaultMessage: 'Modal Überschrift',
    id: 'special-hint.modal-headline',
  },
});

const SpecialHintWrapper = styled.div<{ isTablet: boolean; hasModal: boolean }>`
  margin: 1.5rem 0;
  ${({ isTablet }) => isTablet && 'margin-right: 0.75rem;'};
  ${({ hasModal }) => hasModal && 'cursor: pointer;'}
`;

const ModalEsiContainer = styled.div`
  .modalbox-headline {
    display: none;
  }
`;

interface SpecialHintProps {
  hasModal?: boolean;
}

/**
 * This is the size hint component. If the current product has a size hint available this
 * component renders the size hint component with the according message.
 */
export const SpecialHint: FunctionComponent<SpecialHintProps> = ({
  hasModal = false,
}): ReactElement => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const dispatchGTMEvent = useTracking();
  const config: FragmentConfiguration = useConfig();
  const intl = useIntl();
  const orientation = createMediaMatcher({
    landscape: '(orientation: landscape) and (max-width : 1026px)',
    portrait: '(orientation: portrait) and (min-width : 766px) and (max-width : 1026px)',
  });
  const isTablet = useMedia({
    desktop: false,
    mobile: false,
    tablet: orientation.useMedia({ landscape: true, portrait: true }),
  });
  const esiUrl = useUrlParameters(config.specialHint.esiUrl, undefined, true);
  return (
    <>
      <SpecialHintWrapper
        isTablet={isTablet}
        hasModal={hasModal}
        data-testid="special-hint"
        onClick={() => {
          if (hasModal) {
            setIsModalOpen(true);
            dispatchGTMEvent({
              event: 'ga_event',
              eventAction: 'viewLayer',
              eventCategory: 'specialHintLayer',
              eventLabel: 'detailview',
              eventValue: intl.formatMessage(messages.modalHeadline),
            });
          }
        }}
      >
        <Notification
          level={config.specialHint.hintLevel}
          isInline
          isAnimated={false}
          content={
            <FormattedMessage id="special-hint.notification-text" defaultMessage="Hinweistext" />
          }
        />
      </SpecialHintWrapper>
      {hasModal && (
        <Modal
          data-testid="special-hint-modal"
          open={isModalOpen}
          onRequestClose={() => setIsModalOpen(false)}
          headline={intl.formatMessage(messages.modalHeadline)}
        >
          <ModalEsiContainer data-testid="special-hint-modal-esi-container">
            <Esi src={esiUrl} />
          </ModalEsiContainer>
        </Modal>
      )}
    </>
  );
};

export default SpecialHint;
